import { useState } from 'react';

import {
    IDIOMAS,
    NOMBRE_IDIOMA,
    crearTManual,
    anilloProgreso,
    duracion,
    fechaLarga,
    hora,
    iniciales,
    porcentaje,
    type Asignacion,
} from '@haus/core';

import { Boton } from '../componentes/Boton.js';
import { Hoja, estilosHoja } from '../componentes/Hoja.js';
import { Aviso, BarraAccion, Chip, Contenido, Pantalla, Tarjeta } from '../componentes/estructura.js';
import { empezarLimpieza, sincronizarLimpieza } from '../datos/limpiezas.js';
import { limpiezaAbierta } from '../datos/db.js';
import { useGps } from '../captura/useGps.js';
import { useApp } from '../estado/app.js';
import css from './comun.module.css';

/**
 * 02 · Mis apartamentos de hoy.
 *
 * La pantalla que se abre cada mañana. Trae el día ENTERO en una sola petición
 * —estancias, guiones y checklist incluidos— y lo guarda: a partir de ahí la
 * empleada puede trabajar sin cobertura hasta la noche.
 *
 * Una limpieza que está subiendo cuenta como completada en el anillo. El
 * trabajo está hecho; lo que falta es el transporte, y eso no es asunto de
 * quien limpia.
 */
export function PantallaHoy() {
    const { t, idioma, dia, sesion, ir, cliente, resumenCola, hayRed, cargandoDia, salir } = useApp();
    const tm = crearTManual(idioma);
    const { pedir } = useGps();
    const [menu, setMenu] = useState(false);
    const [arrancando, setArrancando] = useState<string | null>(null);

    const resumen = dia?.resumen ?? { total: 0, completadas: 0, pendientes: 0, subiendo: 0, porcentaje: 0 };
    const porHacer = (a: { estado: string }): boolean =>
        a.estado === 'pendiente' || a.estado === 'en_curso';
    // Lo asignado a ella y lo que esta libre son dos cosas distintas y van en
    // apartados distintos: un piso libre no es trabajo suyo hasta que lo coge,
    // y mezclarlo con el reparto haria que su dia pareciera mas largo de lo que
    // es. El anillo de progreso cuenta lo asignado, no lo libre.
    const pendientes = (dia?.asignaciones ?? []).filter((a) => !a.libre && porHacer(a));
    const libres = (dia?.asignaciones ?? []).filter((a) => a.libre && porHacer(a));
    const hechas = (dia?.asignaciones ?? []).filter((a) => a.estado === 'completada' || a.estado === 'subiendo');
    const horaDelDia = new Date().getHours();

    async function empezar(a: Asignacion): Promise<void> {
        setArrancando(a.assignment_id);
        try {
            // Si ya había una limpieza abierta de esta asignación, se retoma.
            // Pasa cuando la empleada sale de la app a mitad del recorrido.
            const abierta = await limpiezaAbierta();
            if (abierta !== null && abierta.assignment_id === a.assignment_id) {
                ir({ n: 'recorrido', limpieza: abierta.id });

                return;
            }

            // El GPS se pide aquí, al empezar, y no al cerrar: es el momento en
            // que la empleada está en la puerta del apartamento. No se espera
            // si tarda: la limpieza arranca igual.
            const gps = await pedir();
            const modo = a.apartamento.modo_captura === 'fotos' ? 'fotos' : 'video';
            const l = await empezarLimpieza(a, modo, gps);
            void sincronizarLimpieza(cliente, l);
            ir({ n: 'recorrido', limpieza: l.id });
        } finally {
            setArrancando(null);
        }
    }

    return (
        <Pantalla>
            {!hayRed && <Aviso banda>{t('comun.sin_conexion')}</Aviso>}

            <header
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--e12)',
                    padding: 'var(--e14) var(--e22)',
                    paddingTop: 'max(var(--e14), env(safe-area-inset-top))',
                    background: 'var(--bg-surface)',
                    borderBottom: '1px solid var(--line-soft)',
                }}
            >
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={css.pasoSub}>{fechaLarga(new Date(), idioma)}</div>
                    <h1 style={{ fontSize: 'var(--txt-titulo)', fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>
                        {t(horaDelDia < 14 ? 'hoy.saludo' : 'hoy.saludo_tarde', {
                            nombre: (sesion?.empleada.nombre ?? '').split(' ')[0] ?? '',
                        })}
                    </h1>
                </div>
                <span
                    aria-hidden="true"
                    style={{
                        width: 44,
                        height: 44,
                        borderRadius: 999,
                        background: 'var(--accent-bg)',
                        color: 'var(--accent-ink)',
                        fontWeight: 700,
                        fontSize: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {iniciales(sesion?.empleada.nombre ?? '')}
                </span>
            </header>

            <Contenido>
                <Tarjeta>
                    <div className={`${css.tarjetaApto} ${css.progreso}`}>
                        <div>
                            <div style={{ fontSize: 'var(--txt-cuerpo)', fontWeight: 700 }}>
                                {t('hoy.progreso', { hechas: resumen.completadas, total: resumen.total })}
                            </div>
                            <div className={css.pasoSub}>
                                {resumen.subiendo > 0
                                    ? t('hoy.progreso_sub', {
                                          pendientes: resumen.pendientes,
                                          subiendo: resumen.subiendo,
                                      })
                                    : t('hoy.progreso_sub_sin_subida', { pendientes: resumen.pendientes })}
                            </div>
                        </div>
                        <div
                            className={css.anillo}
                            style={{ background: anilloProgreso(resumen.completadas, resumen.total) }}
                        >
                            <span className={css.anilloDisco}>
                                {porcentaje(resumen.completadas, resumen.total)}%
                            </span>
                        </div>
                    </div>
                </Tarjeta>

                {dia === null && cargandoDia && <p className={css.parrafo}>{t('comun.cargando')}</p>}

                {dia !== null && resumen.total === 0 && (
                    <div className={css.vacio}>
                        <strong style={{ fontSize: 'var(--txt-titulo-tarjeta)', color: 'var(--ink)' }}>
                            {t('hoy.vacio')}
                        </strong>
                        <p className={css.parrafo}>{t('hoy.vacio_ayuda')}</p>
                    </div>
                )}

                {pendientes.length > 0 && (
                    <div className={css.grupo}>
                        <span className="etiqueta-mono">{t('hoy.grupo_pendientes')}</span>
                        {pendientes.map((a, i) => (
                            <Tarjeta key={a.assignment_id} data-apartamento={a.apartamento.nombre}>
                                <div className={css.tarjetaApto}>
                                    <div className={css.aptoCabecera}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div className={css.aptoNombre}>{a.apartamento.nombre}</div>
                                            <div className={css.aptoDatos}>
                                                {[
                                                    a.apartamento.direccion,
                                                    a.apartamento.dormitorios !== null
                                                        ? t('hoy.dormitorios', { n: a.apartamento.dormitorios })
                                                        : null,
                                                    a.apartamento.hora_salida !== null
                                                        ? t('hoy.salida', {
                                                              hora: a.apartamento.hora_salida.slice(0, 5),
                                                          })
                                                        : null,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </div>
                                        </div>
                                        {a.urgente && <Chip tono="warn">{t('hoy.urgente')}</Chip>}
                                    </div>
                                    <Boton
                                        variante={i === 0 ? 'primario' : 'secundario'}
                                        disabled={arrancando !== null}
                                        onClick={() => void empezar(a)}
                                    >
                                        {a.estado === 'en_curso' ? t('hoy.continuar') : t('hoy.empezar')}
                                    </Boton>
                                </div>
                            </Tarjeta>
                        ))}
                    </div>
                )}

                {libres.length > 0 && (
                    <div className={css.grupo}>
                        <span className="etiqueta-mono">{t('hoy.grupo_libres')}</span>
                        <p className={css.parrafo}>{t('hoy.grupo_libres_ayuda')}</p>
                        {libres.map((a) => (
                            <Tarjeta key={a.apartamento.id} data-apartamento={a.apartamento.nombre}>
                                <div className={css.tarjetaApto}>
                                    <div className={css.aptoCabecera}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div className={css.aptoNombre}>{a.apartamento.nombre}</div>
                                            <div className={css.aptoDatos}>
                                                {[
                                                    a.apartamento.direccion,
                                                    a.apartamento.dormitorios !== null
                                                        ? t('hoy.dormitorios', { n: a.apartamento.dormitorios })
                                                        : null,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </div>
                                        </div>
                                    </div>
                                    <Boton
                                        variante="secundario"
                                        disabled={arrancando !== null}
                                        onClick={() => void empezar(a)}
                                    >
                                        {a.estado === 'en_curso' ? t('hoy.continuar') : t('hoy.coger')}
                                    </Boton>
                                </div>
                            </Tarjeta>
                        ))}
                    </div>
                )}

                {hechas.length > 0 && (
                    <div className={css.grupo}>
                        <span className="etiqueta-mono">{t('hoy.grupo_completadas')}</span>
                        {hechas.map((a) => {
                            const subiendo = a.estado === 'subiendo';
                            const pct = resumenCola?.actual?.pct ?? 0;
                            const l = a.limpieza;
                            // Hora LOCAL, no la que trae la API. Cortar la
                            // cadena por el carácter 11 daría la hora UTC, que
                            // en verano son dos menos que las del reloj de la
                            // empleada.
                            const marca = l?.ended_at ?? l?.started_at ?? null;
                            const cuando = marca === null ? '' : hora(marca, idioma);

                            return (
                                <div key={a.assignment_id} className={css.fila}>
                                    <span className={`${css.circulo} ${subiendo ? css.circuloAccent : css.circuloOk}`}>
                                        {subiendo ? '↑' : '✓'}
                                    </span>
                                    <div className={css.filaTextos}>
                                        <span className={css.filaTitulo}>{a.apartamento.nombre}</span>
                                        <span className={css.filaSub}>
                                            {subiendo
                                                ? t('hoy.subiendo', { pct })
                                                : l === null
                                                  ? ''
                                                  : l.videos > 0
                                                    ? t('hoy.completada', {
                                                          hora: cuando,
                                                          duracion: duracion(l.video_ms),
                                                      })
                                                    : t('hoy.completada_fotos', { hora: cuando, fotos: l.fotos })}
                                        </span>
                                        {subiendo && (
                                            <div className={css.barraProgreso}>
                                                <div className={css.barraProgresoRelleno} style={{ width: `${pct}%` }} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Contenido>

            <BarraAccion>
                <Boton variante="oscuro" onClick={() => ir({ n: 'qr' })}>
                    ▣ {t('hoy.escanear')}
                </Boton>
                <Boton variante="cuadrado" aria-label={t('hoy.menu')} onClick={() => setMenu(true)}>
                    ☰
                </Boton>
            </BarraAccion>

            {menu && (
                <Hoja titulo={t('hoy.menu')} alCerrar={() => setMenu(false)}>
                    <Boton
                        variante="secundario"
                        onClick={() => {
                            setMenu(false);
                            ir({ n: 'cola' });
                        }}
                    >
                        {t('hoy.menu_cola')}
                        {resumenCola !== null && resumenCola.total - resumenCola.hechas > 0
                            ? ` · ${resumenCola.total - resumenCola.hechas}`
                            : ''}
                    </Boton>
                    {/* El manual, entre el idioma y Salir.
                        En el menu y no en la pantalla del dia: esa se abre cada
                        manana con prisa y no puede llenarse de cosas que se leen
                        una vez. Pero SI dentro del menu, que es el unico sitio
                        donde se busca lo que no se sabe: un manual que no se
                        encuentra es un manual que no existe. */}
                    <Boton
                        variante="secundario"
                        onClick={() => {
                            setMenu(false);
                            ir({ n: 'manual' });
                        }}
                    >
                        {tm('titulo')}
                    </Boton>
                    <MenuIdioma />
                    <Boton
                        variante="peligro"
                        onClick={() => {
                            setMenu(false);
                            void salir();
                        }}
                    >
                        {t('hoy.menu_salir')}
                    </Boton>
                </Hoja>
            )}
        </Pantalla>
    );
}

/**
 * El idioma se guarda por empleada en el servidor, así que aquí solo cambia el
 * de este móvil. Es útil de todas formas: hay quien prefiere trabajar en
 * inglés aunque su ficha diga otra cosa.
 */
function MenuIdioma() {
    const { t, idioma, cambiarAjustes } = useApp();

    return (
        <div>
            <span className="etiqueta-mono">{t('hoy.menu_idioma')}</span>
            <div className={estilosHoja.opciones} style={{ marginTop: 'var(--e8)' }}>
                {IDIOMAS.map((i) => (
                    <button
                        key={i}
                        className={`${estilosHoja.opcion} ${idioma === i ? estilosHoja.opcionActiva : ''}`}
                        onClick={() => void cambiarAjustes({ idioma: i })}
                    >
                        {NOMBRE_IDIOMA[i]}
                    </button>
                ))}
            </div>
        </div>
    );
}
